// src/components/TelegramConfigModal.js
import React, { useState, useEffect } from 'react';
import { FaSave, FaEye, FaEyeSlash, FaTelegramPlane, FaPlus, FaTrash, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { saveTradingBotConfig } from '../redux/slices/tradingSlice';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = 'https://gritwears.store/api';

const TelegramConfigModal = ({ isOpen, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  
  const [botToken, setBotToken] = useState('');
  const [chatIds, setChatIds] = useState(['']);
  const [showToken, setShowToken] = useState(false);
  const [showChatIds, setShowChatIds] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  // Load existing config when modal opens
  useEffect(() => {
    if (isOpen && user?.uid) {
      loadConfig();
    }
  }, [isOpen, user?.uid]);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/trading-bot/config/${user.uid}`);
      const config = response.data;
      if (config.botToken) {
        setBotToken(config.botToken);
        if (config.chatIds && config.chatIds.length > 0) {
          setChatIds([...config.chatIds]);
        } else if (config.chatId) {
          setChatIds([config.chatId]);
        }
        setIsConfigured(true);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  // Add a new chat ID field
  const addChatId = () => {
    setChatIds(prev => [...prev, '']);
  };

  // Remove a chat ID field
  const removeChatId = (index) => {
    setChatIds(prev => {
      if (prev.length <= 1) return [''];
      return prev.filter((_, i) => i !== index);
    });
  };

  // Update a chat ID value
  const updateChatId = (index, value) => {
    setChatIds(prev => {
      const newChatIds = [...prev];
      newChatIds[index] = value;
      return newChatIds;
    });
  };

  const handleSave = async () => {
    if (!botToken) {
      toast.error('Please enter bot token');
      return;
    }
    
    const validChatIds = chatIds.filter(id => id && id.trim());
    if (validChatIds.length === 0) {
      toast.error('Please enter at least one chat ID');
      return;
    }
    
    setConfigLoading(true);
    const config = {
      botToken: botToken.trim(),
      chatIds: validChatIds,
      enabled: true
    };
    
    const success = await dispatch(saveTradingBotConfig(user.uid, config));
    if (success) {
      setIsConfigured(true);
      toast.success(`Saved! Alerts will be sent to ${validChatIds.length} channel(s)`);
      if (onSaved) onSaved(config);
      onClose();
    }
    setConfigLoading(false);
  };

  const handleTest = async () => {
    const validChatIds = chatIds.filter(id => id && id.trim());
    if (!botToken || validChatIds.length === 0) {
      toast.error('Please enter bot token and at least one chat ID');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/trading-bot/telegram/test-multi`, {
        botToken: botToken.trim(),
        chatIds: validChatIds
      });
      
      setTestResult(response.data);
      if (response.data.success) {
        const successCount = response.data.results?.filter(r => r.success).length || 0;
        toast.success(`Test message sent to ${successCount}/${validChatIds.length} channel(s)!`);
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const maskString = (str) => {
    if (!str) return '';
    if (str.length <= 8) return '*'.repeat(str.length);
    return str.substring(0, 4) + '*'.repeat(str.length - 8) + str.substring(str.length - 4);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FaTelegramPlane /> Trading Bot Telegram Configuration</h2>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-body">
          {isConfigured && (
            <div className="configured-badge-modal">
              ✓ Currently sending to {chatIds.filter(id => id && id.trim()).length} channel(s)
            </div>
          )}
          
          <div className="settings-info-modal">
            <p>
              Configure your Telegram bot to receive alerts when you open/close trades on your MetaTrader account.
              Get your bot token from <a href="https://t.me/BotFather" target="_blank">@BotFather</a>.
            </p>
          </div>

          {/* Bot Token Field */}
          <div className="form-group">
            <label>Bot Token:</label>
            <div className="password-input-wrapper">
              <input
                type={showToken ? 'text' : 'password'}
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder="Enter your Telegram bot token"
              />
              <button
                type="button"
                className="toggle-visibility-btn"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {botToken && !showToken && (
              <div className="masked-preview">Token: {maskString(botToken)}</div>
            )}
          </div>

          {/* Multiple Chat IDs Field */}
          <div className="form-group">
            <label>Chat IDs / Channels (Multiple):</label>
            <div className="chat-ids-list-modal">
              {chatIds.map((chatIdValue, index) => (
                <div key={index} className="chat-id-input-group-modal">
                  <input
                    type={showChatIds ? 'text' : 'password'}
                    value={chatIdValue}
                    onChange={(e) => updateChatId(index, e.target.value)}
                    placeholder="Enter chat ID (e.g., -1001234567890) or @username"
                  />
                  {chatIds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeChatId(index)}
                      className="remove-btn"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addChatId} className="add-channel-btn">
              <FaPlus /> Add Another Channel
            </button>
            <div className="help-text">
              <button
                type="button"
                onClick={() => setShowChatIds(!showChatIds)}
                className="toggle-show-btn"
              >
                {showChatIds ? 'Hide' : 'Show'} all Chat IDs
              </button>
            </div>
          </div>

          {/* Test Results */}
          {testResult && (
            <div className={`test-result-modal ${testResult.success ? 'success' : 'error'}`}>
              <div className="test-result-icon">
                {testResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
              </div>
              <div className="test-result-message">
                <strong>{testResult.success ? 'Test Completed!' : 'Test Failed'}</strong>
                <p>{testResult.message}</p>
                {testResult.results && (
                  <div className="test-results-list-modal">
                    {testResult.results.map((r, i) => (
                      <div key={i} className={`result-item ${r.success ? 'success' : 'error'}`}>
                        {r.success ? '✅' : '❌'} {r.chatId} - {r.success ? 'Sent' : r.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary-modal">
            Cancel
          </button>
          <button onClick={handleTest} className="btn-secondary-modal" disabled={testing}>
            {testing ? 'Testing...' : 'Test Channels'}
          </button>
          <button onClick={handleSave} className="btn-primary-modal" disabled={configLoading}>
            <FaSave /> {configLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelegramConfigModal;